import { atom, unstable_createStore } from "jotai";
import { atomWithStorage, createJSONStorage } from "jotai/utils";

import { AddDeviceFormType } from "@/apps/partner/types";
import { FinalFormProps } from "@/components/Onboarding/Steps/Checkout/Checkout";
import { NumberPortingFormProps } from "@/components/Onboarding/Steps/NumberPorting/NumberPorting";
import { BusinessPersonalDetailsFormFields } from "@/components/Onboarding/Steps/PersonalDetails/PersonalDetailsFormFields/PersonalDetailsFormFields";
import { SimVerificationFormProps } from "@/components/Onboarding/Steps/SimVerification/SimVerification";
import { SimTransferStates } from "@/hooks/useSimTransferStateInfo";
import {
  Basket,
  Product,
  SimCardDetail,
  SubscriberOrderResponse,
} from "@/services/model";

import { TransformedProduct } from "./tariffUtils";

/**
 * Persisted DataContext (migrated from old DataContextProvider's manual localStorage usage)
 * Use createJSONStorage to ensure we use JSON storage and session/prod/local behaviour consistent with your other atoms.
 */
export type DataContextInterface = {
  basket?: Basket;
  personalDetails?: BusinessPersonalDetailsFormFields;
  selectedOption?: TransformedProduct;
  numberPorting?: NumberPortingFormProps;
  checkout?: { reoccuringPayment: FinalFormProps["reoccuringPayment"] };
  simVerification?: SimVerificationFormProps;
  allowedMaxOnboardingStep?: number;
  isEmailVerified?: boolean;
};

export const store = unstable_createStore();

export const accessTokenAtom = atomWithStorage<string | null>(
  "accessToken",
  null,
);

export const refreshTokenAtom = atomWithStorage<string | null>(
  "refreshToken",
  null,
);

export const isAnonymousUserAtom = atomWithStorage<boolean>(
  "isAnonymousUser",
  false,
);

export const setAuthAtom = atom(
  null,
  (
    get,
    set,
    payload: {
      accessToken: string | null;
      refreshToken: string | null;
      isAnonymous?: boolean;
      isAgent?: boolean;
    },
  ) => {
    set(accessTokenAtom, payload.accessToken);
    set(refreshTokenAtom, payload.refreshToken);
    set(isAnonymousUserAtom, payload.isAnonymous ?? false);
    set(isAgentAtom, payload.isAgent ?? false);
  },
);

export const clearAuthAtom = atom(null, (get, set) => {
  set(accessTokenAtom, null);
  set(refreshTokenAtom, null);
  set(isAnonymousUserAtom, true);
  set(isAgentAtom, false);
});

export const isAgentAtom = atomWithStorage<boolean>("isAgent", false);

export const verifierAtom = atomWithStorage<string | undefined>(
  "verifier",
  undefined,
);

export const dataContextAtom = atomWithStorage<DataContextInterface>(
  "dataContext",
  {
    allowedMaxOnboardingStep: 1,
    isEmailVerified: false,
  },
  createJSONStorage(() => localStorage),
);

export const destinationAtom = atomWithStorage<string | undefined>(
  "destination",
  undefined,
);

export const managedUnscannedDevicesAtom = atomWithStorage<number[]>(
  "managedUnscannedDevices",
  [],
);
export const tariffCheckoutPaymentPersistenceAtom = atomWithStorage<
  | {
      product?: Product;
      subscriberOrderResponse?: SubscriberOrderResponse;
      simDetail?: SimCardDetail;
    }
  | undefined
>("tariffCheckoutPaymentPersistence", undefined);

export const portalTariffProductIDAtom = atom<number | undefined>(undefined);

export enum AlertTypes {
  "error",
  "info",
  "success",
  "warning",
}

export interface AlertInterface {
  type: AlertTypes;
  title?: string;
  text: string;
}

export const alertListAtom = atom<AlertInterface[]>([]);

export type UserAccountState = {
  editingMail: boolean;
  editingPassword: boolean;
  editingPersonalData: boolean;
  editingPayment: boolean;
  editingBillingInfo: boolean;
  editingSecurity: boolean;
};
export const userAccountStateAtomInitialState: UserAccountState = {
  editingMail: false,
  editingPassword: false,
  editingPersonalData: false,
  editingPayment: false,
  editingBillingInfo: false,
  editingSecurity: false,
};
export const userAccountStateAtom = atom<UserAccountState>(
  userAccountStateAtomInitialState,
);

export const isEditingSimDetailsAtom = atom<boolean>(false);

export const isListViewAtom = atomWithStorage<boolean | undefined>(
  "listView",
  false,
);

export const basketIsUpdatedAtom = atom<boolean>(false);

export const esimTransferTypeAtom = atom<SimTransferStates>({});

export const esimJourneyBasketIdAtom = atomWithStorage<number | null>(
  "esimJourneyBasketId",
  null,
);

export const showsSessionExpiredModalAtom = atom<boolean | undefined>(
  undefined,
);
export const showsPartnerSessionExpiredModalAtom = atom<boolean | undefined>(
  undefined,
);

export const twoFactorBackupCodesAtom = atom<number[]>([]);

export const phoneNumberAtom = atom<string>("");

export const hasChosenStandardLoginAtom = atomWithStorage<boolean>(
  "hasChosenStandardLogin",
  false,
);

// Atom for storing AddDevice form data to be displayed in Summary
export const addDeviceFormDataAtom = atomWithStorage<AddDeviceFormType | null>(
  "addDeviceFormData",
  null,
  createJSONStorage(() => sessionStorage),
);
